import { useCallback, useEffect, useRef, useState } from 'react';
import DOMPurify from 'dompurify';
import styled from 'styled-components';
import { findNotifications, type Notification } from '../../api/notifications';
import { toUserMessage } from '../../api/client';
import { getConfig } from '../../config';
import { Button } from '../../uikit/components/Button';
import { Dropdown } from '../../uikit/components/Dropdown';
import { MenuButton } from '../../uikit/components/MenuButton';
import { Paper } from '../../uikit/components/Paper';
import { IconEvents } from '../../uikit/components/icons';

/** Разрешённые HTML-теги в тексте уведомлений (по контракту API). */
const ALLOWED_NOTIFICATION_TAGS = ['b', 'strong', 'br'];

function sanitizeNotificationHtml(html: string): string {
  return DOMPurify.sanitize(html, { ALLOWED_TAGS: ALLOWED_NOTIFICATION_TAGS });
}

const List = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  max-height: 240px;
  overflow-y: auto;
`;

const ListItem = styled.li`
  padding: ${({ theme }) => theme.space[1]}px ${({ theme }) => theme.space[2]}px;
  font-size: ${({ theme }) => theme.fontSizes[3]}px;
  color: ${({ theme }) => theme.colors.text.primary};
  border-bottom: 1px solid ${({ theme }) => theme.colors.divider};

  &:last-child {
    border-bottom: none;
  }
`;

const EmptyMessage = styled.div`
  padding: ${({ theme }) => theme.space[2]}px;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.fontSizes[3]}px;
`;

const LoadingMessage = styled.div`
  padding: ${({ theme }) => theme.space[2]}px;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.fontSizes[3]}px;
`;

const TriggerWrapper = styled.div`
  position: relative;
  display: inline-flex;
`;

const ShowAllWrapper = styled.div`
  margin-top: ${({ theme }) => theme.space[1]}px;
  width: 100%;
`;

/** Стили badge по образцу web-client (NotificationBadge): синий фон, мелкий текст. */
const Badge = styled.span`
  position: absolute;
  top: ${({ theme }) => theme.space[0]}px;
  right: -4px;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 17px;
  height: 17px;
  padding: 0 ${({ theme }) => theme.space[0]}px;
  font-size: ${({ theme }) => theme.fontSizes[0]}px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.white};
  background: ${({ theme }) => theme.colors.primary.main};
  border: 1px solid ${({ theme }) => theme.colors.background};
  border-radius: 7px;
`;

function getNotificationDisplayContent(n: Notification): string {
  if (n.text) return n.text;
  if (n.name) return n.name;
  return 'Уведомление';
}

export function NotificationsDropdown() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<Notification[]>([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const loadNotifications = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await findNotifications({
        page: 1,
        itemsPerPage: 5,
        isViewed: false,
      });
      setItems(data.items);
      setCount(data.count);
    } catch (err) {
      setError(toUserMessage(err));
      setItems([]);
      setCount(0);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadNotifications();
  }, [loadNotifications]);

  useEffect(() => {
    if (!open) return;

    void loadNotifications();
  }, [open, loadNotifications]);

  useEffect(() => {
    const intervalSec = getConfig().notificationsPollIntervalSeconds ?? 60;
    const intervalMs = Math.max(1000, intervalSec * 1000);

    pollIntervalRef.current = setInterval(() => {
      void loadNotifications();
    }, intervalMs);

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [loadNotifications]);

  const handleShowAll = useCallback(() => {
    setOpen(false);
    window.open(`${getConfig().istockURL}/notifications`, '_blank');
  }, []);

  return (
    <Dropdown
      trigger={
        <TriggerWrapper>
          <MenuButton size={42} aria-label="Уведомления">
            <IconEvents />
          </MenuButton>
          {count > 0 && <Badge>{count > 99 ? '99+' : count}</Badge>}
        </TriggerWrapper>
      }
      open={open}
      onOpenChange={setOpen}
      placement="left-start"
      width="320px"
    >
      <Paper padding={12} shadow={0}>
        {loading && <LoadingMessage>Загрузка...</LoadingMessage>}
        {error && <EmptyMessage role="alert">{error}</EmptyMessage>}
        {!loading && !error && items.length === 0 && (
          <EmptyMessage>Нет новых уведомлений</EmptyMessage>
        )}
        {!loading && !error && items.length > 0 && (
          <>
            <List role="list">
              {items.map((n, i) => (
                <ListItem
                  key={n.id ?? i}
                  dangerouslySetInnerHTML={{
                    __html: sanitizeNotificationHtml(getNotificationDisplayContent(n)),
                  }}
                />
              ))}
            </List>
            <ShowAllWrapper>
              <Button variant="text" color="primary" size="s" onClick={handleShowAll}>
                Показать все
              </Button>
            </ShowAllWrapper>
          </>
        )}
      </Paper>
    </Dropdown>
  );
}
